import { React, useState, useEffect, useRef, memo} from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Container, TextField, Button, Checkbox, Dialog, DialogTitle, DialogContent, Grid } from '@mui/material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import api from './api';
import Header from './components/Header';
import { useConfirmBackLogout } from './common/useConfirmBackLogout';
import BackButton from './common/BackButton';
import { useNavigationHistory } from "./common/navigationHistory.js";
import { validateSelectedFiles } from "./common/fileUploadValidation.js";

const ExhibitorInfo = () => {
    const [exhibitors, setExhibitors] = useState([]);
    const [filteredExhibitors, setFilteredExhibitors] = useState([]);
    const [searchName, setSearchName] = useState('');
    const [formData, setFormData] = useState({
      company_name: '',
      address: '',
      booth_number: '',
      zip_code: '',
      phone_number: '',
      department: '',
      email: '',
      user_name: ''
    });
    const [plainPassword, setPlainPassword] = useState('');
    const [page, setPage] = useState(0);
    const itemsPerPage = 5;
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = Math.ceil(filteredExhibitors.length / itemsPerPage)
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [resendTarget, setResendTarget] = useState(null);      // メール再送用
    const [selectedExhibitors, setSelectedExhibitors] = useState([]);
    const [mailTarget, setMailTarget] = useState(null);
    const [mailSubject, setMailSubject] = useState("");
    const [mailBody, setMailBody] = useState("");
    const [loading, setLoading] = useState(false);
    const [mailFiles, setMailFiles] = useState([]);
    const navigate = useNavigate();
    const handleLogout = () => {
      localStorage.removeItem('user');
      sessionStorage.clear(); // ← これ追加！
      navigate('/login/host');
    };
     const [showFormMenu, setShowFormMenu] = useState(false);

    const paginatedFilters = filteredExhibitors.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );

    const [editTarget, setEditTarget] = useState(null);

    const fetchExhibitors = async () => {
      try {
        const res = await api.get('/api/exhibitors');
        setExhibitors(res.data);
        setFilteredExhibitors(res.data);
      } catch (err) {
        console.error('出展者一覧の取得に失敗:', err);
      }
    };    

    const handleSaveEdit = async () => {
      try {
        await api.put(`/api/exhibitor/${editTarget.exhibitor_id}`, {
          company_name: editTarget.company_name,
          address: editTarget.address,
          phone_number: editTarget.phone_number,
          email: editTarget.email,
          user_name: editTarget.user_name,
          booth_number: editTarget.booth_number,
          zip_code: editTarget.zip_code,
          memo: editTarget.memo
        });

        alert('出展者情報を更新しました');
        setEditTarget(null);

        fetchExhibitors();
      } catch (err) {
        console.error('更新失敗:', err);
        alert('出展者情報の更新に失敗しました');
      }
    };
    

    const handleInputChange = (e) => {
      setFormData({...formData, [e.target.name]: e.target.value});
    };

    const handleRegister = async (e) => {
      e.preventDefault();
      try {
        const res = await api.post('/api/exhibitors/register', formData);
        alert('登録完了');
        setPlainPassword('登録が完了しました。初期パスワードはメールで安全に通知してください。');
    
        // 一覧更新
        const updated = await api.get('/api/exhibitors');
        setExhibitors(updated.data);
        setFilteredExhibitors(updated.data);
      } catch (err) {
        console.error('登録エラー:', err);
        alert('登録失敗しました');
      }
    };
    
    useEffect(() => {
        api.get('/api/exhibitors')
          .then(res => {
            setExhibitors(res.data);
            setFilteredExhibitors(res.data);
          })
          .catch(err => console.error('出展者情報の取得失敗:', err));
      }, []);
      
    useEffect(() => {
      const filtered = exhibitors.filter(ex =>
        ex.company_name.includes(searchName)
      );
      setFilteredExhibitors(filtered);
      setPage(0);
    }, [searchName, exhibitors]);

    const fileInputRef = useRef(null);

    const triggerFileInput = () => {
      if (fileInputRef.current) {
        fileInputRef.current.click(); // inputをクリックさせる
      }
    };

    const handleExcelUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const validation = validateSelectedFiles([file]);
      if (!validation.valid) {
        alert(validation.message);
        e.target.value = null;
        return;
      }
      const formData = new FormData();
      formData.append("file", file);

      try {
        const res = await api.post('/api/exhibitors/bulk-upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'blob',
        });

        const updatedFileBlob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = window.URL.createObjectURL(updatedFileBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'backup.xlsx');
        document.body.appendChild(link);
        link.click();
      } catch (err) {
        console.error('Excelアップロード失敗:', err);
        alert('Excelアップロードに失敗しました');
      }
    };

    const handleResendSubmit = async (exhibitor) => {
      try {
        // stateに保存（必要なら UI側にも反映される）
        setResendTarget(exhibitor);


        await api.post('/api/send-password-resend', {
          exhibitor_id: exhibitor.exhibitor_id,
          company_name: exhibitor.company_name,
          subject: emailSubject,
          body: emailBody,
        });

        alert('メールを送信しました');
        setShowEmailForm(false);
        setResendTarget(null);
      } catch (err) {
        console.error('送信失敗:', err);
        alert('メール送信に失敗しました');
      }
    };


    useEffect(() => {
      const fetchTemplate = async () => {
        try {
          const templateRes = await api.get('/api/mail-templates/notification');
          const template = templateRes.data;

          setMailSubject(template.subject || "");
          setMailBody(template.body || "");
        } catch (err) {
          console.error("テンプレート取得失敗", err);
          alert("通知テンプレートの取得に失敗しました");
        }
      };

      fetchTemplate();
    }, []);

    // 事故防止用　状態リセット共通化
    const resetMailState = () => {
      setMailTarget(null);
      setSelectedExhibitors([]);
      setMailSubject("");
      setMailBody("");
    };

    // メール送信機能（送信対象選択＆メール送信機能）
    const handleNotificationSend = async () => {
      const count = selectedExhibitors.length;

      if (count === 0) {
        alert("送信対象が選択されていません。");
        return;
      }

      const ok = window.confirm(
        `${count}件に通知メールを送信します。\n本当に送信しますか？`
      );
      if (!ok) return;

      try {
        const formData = new FormData();

        formData.append("subject", mailSubject);
        formData.append("body", mailBody);
        formData.append(
          "exhibitor_ids",
          JSON.stringify(selectedExhibitors)
        );

        // 添付ファイル
        mailFiles.forEach((file) => {
          formData.append("files", file);
        });

        const res = await api.post(
          "/api/send-notification",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );


        if (res.status === 200) {
          alert("通知メールを送信しました！");
        }
      } catch (err) {
        console.error(err);
        alert("送信失敗しました。");
      }
    };

    // 　　　　　　　　　　・・・
    // お知らせメール・・・テスト・・・送信機能
    // 　　　　　　　　　　・・・
    const handleNotificationTestSend = async () => {
      if (!mailSubject || !mailBody) {
        alert("件名・本文を入力してください。");
        return;
      }

      const ok = window.confirm(
        "テストメールを送信します。\n（選択中の出展者には送信されません）"
      );
      if (!ok) return;

      try {
        const formData = new FormData();

        formData.append("subject", mailSubject);
        formData.append("body", mailBody);

        mailFiles.forEach((file) => {
          formData.append("files", file);
        });

        const res = await api.post(
          "/api/send-notification-test",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        if (res.status === 200) {
          alert("テストメールを送信しました！");
        }
      } catch (err) {
        console.error(err);
        alert("テスト送信に失敗しました。");
      }
    };

    // 個別チェック
    const handleCheckboxChange = (ex) => {
      setSelectedExhibitors((prev) => {
        let newSelected;

        if (prev.includes(ex.exhibitor_id)) {
          newSelected = prev.filter((id) => id !== ex.exhibitor_id);
        } else {
          newSelected = [...prev, ex.exhibitor_id];
        }

        if (newSelected.length > 0) {
          const firstSelectedCompany = filteredExhibitors.find(
            (e) => e.exhibitor_id === newSelected[0]
          );

          setMailTarget({
            type: "notification",
            company: firstSelectedCompany,
            openedBy: "checkbox", // ★重要
          });
        } else {
          setMailTarget(null);
        }

        return newSelected;
      });
    };

    // 一括チェック
    const handleSelectAll = () => {
      if (selectedExhibitors.length === filteredExhibitors.length) {
        // すでに全選択なら解除
        setSelectedExhibitors([]);
        setMailTarget(null);
      } else {
        // 全選択
        const allIds = filteredExhibitors.map((ex) => ex.exhibitor_id);
        setSelectedExhibitors(allIds);
        setMailTarget({
          type: "notification",
          company: filteredExhibitors[0],
        });
      }
    };

    // メール送信ログを確認
    const handleDownloadLatestLog = async () => {
    try {
      const res = await api.get(
        "/api/mail/latest-log",
        {
          paramas: {job_type: "notification"},
          responseType: "blob" // ★ ここ超重要
        }
      );

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "お知らせメール_最新メール送信ログ.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error(err);
      alert("最新送信ログのダウンロードに失敗しました");
    }
  };

  useConfirmBackLogout(navigate);
  useNavigationHistory();

return <div>
          <Header
            title="SDGs子ども・ユースフェア 出展者情報"
          />
          <Container sx={{ mt: 5 }}>
            <Box sx={{ display: 'flex' }}>
              {/* 左メニュー */}
              <Box sx={{ width: '20%', pr: 2, borderRight: '1px solid #ccc' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box className="menu-item" onClick={() => navigate('/host-dashboard')}>
                    申請状況
                  </Box>
                  <Box
                    className="menu-group"
                    id="hover-block"
                    onMouseEnter={() => {}}
                    onMouseLeave={() => {}}
                  >
                    <Box className="menu-item reverse-triangle" onClick={() => setShowFormMenu(prev => !prev)}>
                      会社情報
                    </Box>
                      {showFormMenu && (
                        <Box className="hover-menu">
                          <Typography
                            sx={{ color: '#0071bc', textAlign: 'center' }}
                            className='form-button'
                            onClick={() => navigate('/exhibitor-info')}
                          >
                            出展者
                          </Typography>
                          <Typography
                            sx={{ color: '#0071bc', textAlign: 'center' }}
                            className='form-button'
                            onClick={() => navigate('/corporate-info')}
                          >
                            協力会社
                          </Typography>
                        </Box>
                      )}
                  </Box>
                  <Box className="menu-item" onClick={() => navigate('/create-mail')}>
                    メール作成
                  </Box>
                  <Box className="menu-item" onClick={() => navigate('/create-notice')}>
                    お知らせ作成
                  </Box>
                  <Box className="menu-item" onClick={() => navigate('/form-history')}>
                    再提出ログ確認
                  </Box>
                </Box>
                <BackButton />
              </Box>
  
              {/* メインコンテンツ（右） */}
              <Box sx={{ width: '80%', pl: 2 }}>
                <Typography variant="h6" gutterBottom className='title'>出展者情報検索</Typography>
                <TextField
                label="会社名で検索"
                variant="outlined"
                fullWidth
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                sx={{ mb: 2 , mt: 3 }}
                />
                <TableContainer component={Box}>
                <Table sx={{ width: '150%', overflowX: 'hidden' }}>
                    <TableHead>
                    <TableRow>
                        {/* <TableCell padding="checkbox">
                          メール
                        </TableCell> */}
                        <TableCell>会社名</TableCell>
                        <TableCell>小間番号</TableCell>
                        <TableCell>郵便番号</TableCell>
                        <TableCell>住所</TableCell>
                        <TableCell>電話番号</TableCell>
                        <TableCell>担当者名</TableCell>
                        <TableCell>メールアドレス</TableCell>
                        <TableCell>メモ</TableCell>
                        <TableCell>操作</TableCell>
                        <TableCell>パスワード再送</TableCell>
                    </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedFilters
                        .slice(page * itemsPerPage, (page + 1) * itemsPerPage)
                        .map((ex, index) => (
                          <TableRow key={index}>
                            {/* <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedExhibitors.includes(ex.exhibitor_id)}
                                onChange={() => handleCheckboxChange(ex)}
                              />
                            </TableCell> */}
                            <TableCell>{ex.company_name}</TableCell>
                            <TableCell>{ex.booth_number}</TableCell>
                            <TableCell>{ex.zip_code}</TableCell>
                            <TableCell>{ex.address}</TableCell>
                            <TableCell>{ex.phone_number}</TableCell>
                            <TableCell>{ex.user_name}</TableCell>
                            <TableCell>{ex.email}</TableCell>
                            <TableCell>{ex.memo}</TableCell>
                            <TableCell>
                              <Button variant="outlined" onClick={() => {setEditTarget(ex); }}>
                                編集
                              </Button>
                            </TableCell>
                            <TableCell>
                              <Button variant="outlined" onClick={() => handleResendSubmit(ex)}>選択</Button>
                            </TableCell>
                          </TableRow>
                      ))}
                    </TableBody>
                </Table>
                </TableContainer>
                {/* {mailTarget?.type === "notification" && (
                  <Box mt={4} p={2} border="1px solid #ccc" borderRadius={2}>
                    <Typography variant="h6">
                      通知メールの送信
                      {mailTarget.openedBy === "checkbox" &&
                        `（${selectedExhibitors.length} 件選択中）`}
                      {mailTarget.openedBy === "test" && "（テスト）"}
                    </Typography>

                    <TextField
                      label="件名"
                      fullWidth
                      margin="normal"
                      value={mailSubject}
                      onChange={(e) => setMailSubject(e.target.value)}
                    />

                    <TextField
                      label="本文"
                      fullWidth
                      margin="normal"
                      multiline
                      minRows={6}
                      value={mailBody}
                      onChange={(e) => setMailBody(e.target.value)}
                    />

                    <input
                      type="file"
                      multiple
                      accept=".pdf,.txt,.xlsx,.pptx,.docx"
                      onChange={(e) => {
                        const validation = validateSelectedFiles(e.target.files);
                        if (!validation.valid) {
                          alert(validation.message);
                          e.target.value = null;
                          return;
                        }
                        setMailFiles(validation.files);
                      }}
                    />

                    <Box mt={2} display="flex" gap={2}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNotificationSend}
                        disabled={mailTarget.openedBy !== "checkbox"}
                      >
                        送信
                      </Button>

                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={handleNotificationTestSend}
                        disabled={mailTarget.openedBy !== "test"}
                      >
                        テスト送信
                      </Button>

                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => {
                          setMailTarget(null);
                          setSelectedExhibitors([]);
                          setMailSubject("");
                          setMailBody("");
                        }}
                      >
                        キャンセル
                      </Button>
                    </Box>
                  </Box>
                )} */}
                {editTarget && (
                  <Box sx={{ mt: 4, p: 2, border: '1px solid #ccc', borderRadius: '8px' }}>
                    <Typography variant="h6">出展者情報の編集</Typography>
                    <TextField
                      label="会社名"
                      fullWidth
                      value={editTarget.company_name}
                      onChange={(e) =>
                        setEditTarget({ ...editTarget, company_name: e.target.value })
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="ブース番号"
                      fullWidth
                      value={editTarget.booth_number || ''}
                      onChange={(e) =>
                        setEditTarget({ ...editTarget, booth_number: e.target.value })
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="郵便番号"
                      fullWidth
                      value={editTarget.zip_code || ''}
                      onChange={(e) =>
                        setEditTarget({ ...editTarget, zip_code: e.target.value })
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="住所"
                      fullWidth
                      value={editTarget.address}
                      onChange={(e) =>
                        setEditTarget({ ...editTarget, address: e.target.value })
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="電話番号"
                      fullWidth
                      value={editTarget.phone_number}
                      onChange={(e) =>
                        setEditTarget({ ...editTarget, phone_number: e.target.value })
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="担当者名"
                      fullWidth
                      value={editTarget.user_name}
                      onChange={(e) =>
                        setEditTarget({ ...editTarget, user_name: e.target.value })
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="メールアドレス"
                      fullWidth
                      value={editTarget.email}
                      onChange={(e) =>
                        setEditTarget({ ...editTarget, email: e.target.value })
                      }
                      sx={{ mb: 2 }}
                    />

                    <TextField
                      label="メモ欄"
                      fullWidth
                      multiline
                      rows={4}
                      value={editTarget.memo}
                      onChange={(e) =>
                        setEditTarget({ ...editTarget, memo: e.target.value })
                      }
                      sx={{ mb: 2 }}
                    />

                    <Button variant="contained" onClick={handleSaveEdit}>
                      保存する
                    </Button>
                    <Button variant="text" color="error" onClick={() => setEditTarget(null)} sx={{ ml: 2 }}>
                      キャンセル
                    </Button>
                  </Box>
                )}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Button
                    variant="outlined"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => prev - 1)}
                  >
                    前へ
                  </Button>
                  <span style={{ lineHeight: '40px' }}>ページ {currentPage} / {totalPages}</span>
                  <Button
                    variant="outlined"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                  >
                    次へ
                  </Button>
                </Box>
                {/* <Box display="flex" gap={2} mt={2}>
                  <Button variant="outlined" onClick={handleSelectAll}>
                    {selectedExhibitors.length === filteredExhibitors.length
                      ? "メール送信先全解除"
                      : "メール送信先全選択"}
                  </Button>

                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => {
                      setMailTarget({
                        type: "notification",
                        company: null,
                        openedBy: "test", // ★テスト起点
                      });
                    }}
                  >
                    テストメール作成
                  </Button>
                </Box>
                <Box py={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDownloadLatestLog}
                  >
                    最新送信ログをダウンロード
                  </Button>
                </Box> */}
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" className='title'>新規出展者登録</Typography>
                    <Box component="form" onSubmit={handleRegister} sx={{ mt: 2 }}>
                      {/* {['company_name', 'booth_number', 'zip_code', 'address', 'phone_number', 'department', 'email', 'user_name'].map((field) => (
                      <TextField
                          key={field}
                          label={field.replace('_', ' ').toUpperCase()}
                          name={field}
                          fullWidth
                          margin="normal"
                          value={formData[field]}
                          onChange={handleInputChange}
                      />
                      ))}
                      <Button type="submit" variant="contained" sx={{ mt: 2 }}>登録</Button> */}
                      <Box sx={{mt: 2}}>
                        <img
                          src="/excel-ul.png"
                          alt="Excelアップロード"
                          onClick={triggerFileInput}
                          style={{
                            cursor: "pointer",
                            opacity: 1,
                            width: "200px",
                            marginTop: "10px",
                          }}
                        />

                        {/* 実際のfile inputは非表示 */}
                        <input
                          type="file"
                          accept=".xlsx"
                          ref={fileInputRef}
                          style={{ display: "none" }}
                          onChange={handleExcelUpload}
                        />
                        <p className="text-sm text-gray-500">※Excelファイル（.xlsx）のみ対応</p>
                      </Box>
                      {plainPassword && (
                      <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                          {plainPassword}
                      </Typography>
                      )}
                    </Box>
                </Box>
              </Box>
            </Box>
          </Container>
          <footer>
            <Typography>
              © 2026 THE MID-JAPAN ECONOMIST.
            </Typography>
          </footer>
        </div>;
};

export default ExhibitorInfo;
